import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Grid,
  Chip,
  Avatar,
  Box,
  Button,
  Switch,
  FormControlLabel,
  Pagination,
} from "@mui/material";

import { useUserLocation } from "./contexts/LocationContext";
import { useSearch } from "./contexts/SearchContext";
import ListingDetailsModal from "./ListingDetailsModal";
import TicketPurchase from "./TicketPurchase";
import VenueBookingFormModal from "./VenueBookingFormModal";
import PerformanceBookingFormModal from "./PerformanceBookingFormModal";
import ServiceBookingFormModal from "./ServiceBookingFormModal";
import ScreenLoader from "./components/ScreenLoader";

import { getAvatarUrl } from "./utils/avatar";

const FILTER_TAGS = [
  { label: "All", value: "all" },
  { label: "Users", value: "user" },
  { label: "Events", value: "event" },
  { label: "Venues", value: "venue" },
  { label: "Entertainment", value: "performance" },
  { label: "Services", value: "service" },
];

const ITEMS_PER_PAGE = 8;

const toRad = (v) => (v * Math.PI) / 180;

const getDistanceKm = (a, b) => {
  if (!a || !b) return null;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);

  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
};

const isFutureDate = (date) =>
  date ? new Date(date).getTime() > Date.now() : false;

const SearchResults = ({ results, onViewProfile }) => {
  const { searchQuery, nearMe, setNearMe, searchLoading } = useSearch();
  const userLocation = useUserLocation();

  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [pagination, setPagination] = useState({});

  if (searchLoading) {
    return (
      <ScreenLoader
        open={true}
        text="Searching"
        subText="Finding results across all categories..."
      />
    );
  }

  const handleImageError = (e) => {
    e.target.onerror = null;
    e.target.src = process.env.REACT_APP_AVATAR_URL;
  };

  const query = searchQuery.toLowerCase();

  const filteredResults = results
    .map((item) => {
      if (
        nearMe &&
        userLocation &&
        item.location?.coordinates?.length === 2
      ) {
        const [lng, lat] = item.location.coordinates;
        const distanceKm = getDistanceKm(userLocation, { lat, lng });
        return { ...item, distanceKm };
      }
      return item;
    })
    .filter((item) => {
      if (filter !== "all" && item.type !== filter) return false;
      if (!JSON.stringify(item).toLowerCase().includes(query)) return false;

      if (item.type === "event") {
        if (!isFutureDate(item.date || item.startDate)) return false;
      }

      if (nearMe) {
        if (item.distanceKm == null) return false;
        if (item.distanceKm > 100) return false;
      }

      return true;
    })
    .sort((a, b) => {
      if (nearMe && a.distanceKm != null && b.distanceKm != null) {
        return a.distanceKm - b.distanceKm;
      }
      return 0;
    });

  const grouped = {
    user: filteredResults.filter(r => r.type === "user"),
    event: filteredResults.filter(r => r.type === "event"),
    venue: filteredResults.filter(r => r.type === "venue"),
    performance: filteredResults.filter(r => r.type === "performance"),
    service: filteredResults.filter(r => r.type === "service"),
  };
  
  return (
    <Box sx={{ p: 2 }}>

      {/* FILTER CHIPS */}
      <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
        {FILTER_TAGS.map(tag => (
          <Chip
            key={tag.value}
            label={tag.label}
            color={filter === tag.value ? "primary" : "default"}
            onClick={() => setFilter(tag.value)}
          />
        ))}
      </Box>

      {/* NEAR ME SWITCH */}
      <FormControlLabel
        control={
          <Switch
            checked={nearMe}
            onChange={(e) => {
              if (e.target.checked && !userLocation) {
                alert("Please enable location access to use Near Me search.");
                return;
              }
              setNearMe(e.target.checked);
            }}
          />
        }
        label="Near Me"
      />

      {/* RESULTS */}
      {Object.keys(grouped).map(type => {
        const list = grouped[type];
        if (!list.length) return null;

        const page = pagination[type] || 1;
        const pageCount = Math.ceil(list.length / ITEMS_PER_PAGE);
        const paginatedList = list.slice(
          (page - 1) * ITEMS_PER_PAGE,
          page * ITEMS_PER_PAGE
        );

        return (
          <Box key={type} sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
              {type.charAt(0).toUpperCase() + type.slice(1)}s ({list.length})
            </Typography>

            <Grid container spacing={2}>
              {paginatedList.map((item) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={item._id}>
                  <Card
                    sx={{ cursor: "pointer" }}
                    onClick={() => setSelected(item)}
                  >
                    {/* USER CARD */}
                    {item.type === "user" ? (
                      <CardContent sx={{ textAlign: "center" }}>
                        <Avatar
                          src={getAvatarUrl(item)}
                          onError={handleImageError}
                          sx={{ width: 100, height: 100, margin: "auto", mb: 1 }}
                        />
                        <Typography variant="h6">
                          {item.username}
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ mt: 1 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewProfile(item._id);
                          }}
                        >
                          View Profile
                        </Button>
                      </CardContent>
                    ) : (
                      <>
                        <CardMedia
                          component="img"
                          image={item.image || item.images?.[0]?.url}
                          height="160"
                          sx={{ objectFit: "cover" }}
                        />
                        <CardContent>
                          {nearMe && item.distanceKm != null && (
                            <Chip
                              size="small"
                              label={`${item.distanceKm.toFixed(1)} km away`}
                              sx={{ mt: 0.5 }}
                            />
                          )}
                          <Typography variant="subtitle1" fontWeight="bold">
                            {item.title || item.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.description ||
                              `${item.city || ""} ${item.country || ""}`}
                          </Typography>
                        </CardContent>
                      </>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>

            {pageCount > 1 && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={(_, newPage) =>
                    setPagination({
                      ...pagination,
                      [type]: newPage,
                    })
                  }
                />
              </Box>
            )}
          </Box>
        );
      })}

      {selected?.type === "event" && !selected?.modal && (
        <ListingDetailsModal
          open={!!selected}
          type="event"
          data={selected}
          onClose={() => setSelected(null)}
          onAction={() => setSelected({ ...selected, modal: "ticket" })}
        />
      )}

      {selected?.modal === "ticket" && (
        <TicketPurchase
          event={selected}
          open
          onClose={() => setSelected(null)}
        />
      )}

      {selected?.type === "venue" && !selected?.modal && (
        <ListingDetailsModal
          open={!!selected}
          type="venue"
          data={selected}
          onClose={() => setSelected(null)}
          onAction={() => setSelected({ ...selected, modal: "venue-book" })}
        />
      )}

      {selected?.modal === "venue-book" && (
        <VenueBookingFormModal
          venue={selected}
          open
          onClose={() => setSelected(null)}
        />
      )}

      {selected?.type === "performance" && !selected?.modal && (
        <ListingDetailsModal
          open={!!selected}
          type="performance"
          data={selected}
          onClose={() => setSelected(null)}
          onAction={() =>  setSelected({ ...selected, modal: "performance-book" })}
        />
      )}

      {selected?.modal === "performance-book" && (
        <PerformanceBookingFormModal
          performance={selected}
          open
          onClose={() => setSelected(null)}
        />
      )}

      {selected?.type === "service" && !selected?.modal && (
        <ListingDetailsModal
          open={!!selected}
          type="service"
          data={selected}
          onClose={() => setSelected(null)}
          onAction={() => setSelected({ ...selected, modal: "service-book" })}
        />
      )}

      {selected?.modal === "service-book" && (
        <ServiceBookingFormModal
          service={selected}
          open
          onClose={() => setSelected(null)}
        />
      )}
    </Box>
  );
};

export default SearchResults;
